import * as React from "react"
import { useEffect, useRef, useState } from "react"
import { Link } from "gatsby"
import { StaticImage } from "gatsby-plugin-image"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import { AnimatePresence, motion, useInView } from "motion/react"

interface NavbarProps {
  pathname?: string
  footerRef?: React.RefObject<HTMLElement | null>
}

const MENU_MOTION = {
  duration: 0.22,
  ease: [0.4, 0, 0.2, 1] as const,
}

const NAV_HIDE_MOTION = {
  duration: 0.28,
  ease: [0.4, 0, 0.2, 1] as const,
}

const ICON_MOTION = {
  duration: 0.15,
  ease: "easeOut" as const,
}

const Navbar: React.FC<NavbarProps> = ({ pathname = "/", footerRef }) => {
  const normalizedPath =
    pathname.replace(/\/$/, "") === "" ? "/" : pathname.replace(/\/$/, "")
  const isIntroduction = normalizedPath === "/"
  const isArchitecture = normalizedPath === "/architecture"
  const isPlaybook = normalizedPath === "/playbook"
  const isFoundation = normalizedPath === "/foundation"
  const isVoice = normalizedPath === "/voice"
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const mobileMenuPanelRef = useRef<HTMLDivElement | null>(null)
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null)

  const footerInView = useInView(footerRef ?? { current: null }, {
    amount: 0.12,
  })

  const hideNavForFooter = Boolean(footerRef) && footerInView

  useEffect(() => {
    if (footerInView) {
      setMobileMenuOpen(false)
    }
  }, [footerInView])

  useEffect(() => {
    if (!mobileMenuOpen) return

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target) return
      if (mobileMenuPanelRef.current?.contains(target)) return
      if (mobileMenuButtonRef.current?.contains(target)) return
      setMobileMenuOpen(false)
    }

    document.addEventListener("mousedown", onPointerDown)
    document.addEventListener("touchstart", onPointerDown)
    return () => {
      document.removeEventListener("mousedown", onPointerDown)
      document.removeEventListener("touchstart", onPointerDown)
    }
  }, [mobileMenuOpen])

  const menuTransition = {
    duration: MENU_MOTION.duration,
    ease: MENU_MOTION.ease,
  }

  const navHideTransition = {
    duration: NAV_HIDE_MOTION.duration,
    ease: NAV_HIDE_MOTION.ease,
  }

  const iconTransition = {
    duration: ICON_MOTION.duration,
    ease: ICON_MOTION.ease,
  }

  return (
    <motion.header
      className="fixed inset-x-0 top-0 z-50 border-b border-space-indigo-200 bg-white"
      aria-hidden={hideNavForFooter}
      initial={false}
      animate={{
        y: hideNavForFooter ? "-100%" : "0%",
        opacity: hideNavForFooter ? 0 : 1,
      }}
      transition={navHideTransition}
      style={{
        pointerEvents: hideNavForFooter ? "none" : "auto",
      }}
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex items-center justify-between px-4 py-4 md:py-6">
          <Link to="/" className="block">
            <span className="sr-only">Home</span>
            <StaticImage
              src="../images/Rich Logo_821BRIDGE_no_pad.png"
              alt="821BRIDGE"
              className="w-auto"
              imgClassName="w-auto"
              height={20}
              placeholder="none"
            />
          </Link>

          <div className="flex items-center gap-4">
            <nav aria-label="Global" className="hidden md:block">
              <ul className="flex items-center justify-end gap-6 text-base">
                <li>
                  <Link
                    to="/"
                    className={`nav-link-1 flex items-center gap-2 transition hover:text-space-indigo-500/75 ${
                      isIntroduction
                        ? "!text-space-indigo-900 font-semibold nav-link-active"
                        : "text-space-indigo-500"
                    }`}
                  >
                    <span
                      className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-300"
                      aria-hidden
                    />
                    Introduction
                  </Link>
                </li>

                <li className="flex items-center gap-6">
                  <Link
                    to="/architecture"
                    className={`nav-link-2 flex items-center gap-2 transition hover:text-space-indigo-500/75 ${
                      isArchitecture
                        ? "!text-space-indigo-900 font-semibold nav-link-active"
                        : "text-space-indigo-500"
                    }`}
                  >
                    <span
                      className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                      aria-hidden
                    />
                    Architecture
                  </Link>
                  <Link
                    to="/playbook"
                    className={`nav-link-3 flex items-center gap-2 transition hover:text-space-indigo-500/75 ${
                      isPlaybook
                        ? "!text-space-indigo-900 font-semibold nav-link-active"
                        : "text-space-indigo-500"
                    }`}
                  >
                    <span
                      className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                      aria-hidden
                    />
                    Playbook
                  </Link>
                  <Link
                    to="/foundation"
                    className={`nav-link-4 flex items-center gap-2 transition hover:text-space-indigo-500/75 ${
                      isFoundation
                        ? "!text-space-indigo-900 font-semibold nav-link-active"
                        : "text-space-indigo-500"
                    }`}
                  >
                    <span
                      className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                      aria-hidden
                    />
                    Foundation
                  </Link>
                  <Link
                    to="/voice"
                    className={`nav-link-5 flex items-center gap-2 transition hover:text-space-indigo-500/75 ${
                      isVoice
                        ? "!text-space-indigo-900 font-semibold nav-link-active"
                        : "text-space-indigo-500"
                    }`}
                  >
                    <span
                      className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                      aria-hidden
                    />
                    Voice
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="block md:hidden">
              <button
                type="button"
                ref={mobileMenuButtonRef}
                className="p-2 text-space-indigo-700 transition bg-space-indigo-50 hover:bg-space-indigo-100 cursor-pointer"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                onClick={() => setMobileMenuOpen((v) => !v)}
              >
                <span className="relative block size-6">
                  <AnimatePresence mode="wait" initial={false}>
                    {mobileMenuOpen ? (
                      <motion.span
                        key="close"
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{ opacity: 0, rotate: -90, scale: 0.85 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        exit={{ opacity: 0, rotate: 90, scale: 0.85 }}
                        transition={iconTransition}
                      >
                        <XMarkIcon className="size-6" aria-hidden />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="open"
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{ opacity: 0, rotate: 90, scale: 0.85 }}
                        animate={{ opacity: 1, rotate: 0, scale: 1 }}
                        exit={{ opacity: 0, rotate: -90, scale: 0.85 }}
                        transition={iconTransition}
                      >
                        <Bars3Icon className="size-6" aria-hidden />
                      </motion.span>
                    )}
                  </AnimatePresence>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence initial={false}>
        {mobileMenuOpen ? (
          <motion.div
            key="mobile-menu-layer"
            className="fixed inset-0 top-18 z-40 md:hidden"
            aria-hidden={false}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={menuTransition}
          >
            <div
              id="mobile-menu"
              ref={mobileMenuPanelRef}
              className="w-full border-b border-space-indigo-200 bg-white shadow-sm"
              role="dialog"
              aria-modal="true"
            >
              <nav
                aria-label="Mobile"
                className="mx-auto max-w-7xl border-t border-space-indigo-200 px-4 py-6"
              >
                <ul className="flex flex-col gap-3 text-base">
                  <li>
                    <Link
                      to="/"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`nav-link-1 flex items-center gap-2 w-auto transition hover:text-space-indigo-500/75 ${
                        isIntroduction
                          ? "!text-space-indigo-900 font-semibold nav-link-active"
                          : "text-space-indigo-500"
                      }`}
                    >
                      <span
                        className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-300"
                        aria-hidden
                      />
                      Introduction
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/architecture"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`nav-link-2 flex items-center gap-2 w-auto transition hover:text-space-indigo-500/75 ${
                        isArchitecture
                          ? "!text-space-indigo-900 font-semibold nav-link-active"
                          : "text-space-indigo-500"
                      }`}
                    >
                      <span
                        className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                        aria-hidden
                      />
                      Architecture
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/playbook"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`nav-link-3 flex items-center gap-2 w-auto transition hover:text-space-indigo-500/75 ${
                        isPlaybook
                          ? "!text-space-indigo-900 font-semibold nav-link-active"
                          : "text-space-indigo-500"
                      }`}
                    >
                      <span
                        className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                        aria-hidden
                      />
                      Playbook
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/foundation"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`nav-link-4 flex items-center gap-2 w-auto transition hover:text-space-indigo-500/75 ${
                        isFoundation
                          ? "!text-space-indigo-900 font-semibold nav-link-active"
                          : "text-space-indigo-500"
                      }`}
                    >
                      <span
                        className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                        aria-hidden
                      />
                      Foundation
                    </Link>
                  </li>

                  <li>
                    <Link
                      to="/voice"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`nav-link-5 flex items-center gap-2 w-auto transition hover:text-space-indigo-500/75 ${
                        isVoice
                          ? "!text-space-indigo-900 font-semibold nav-link-active"
                          : "text-space-indigo-500"
                      }`}
                    >
                      <span
                        className="nav-dot h-2 w-2 shrink-0 rounded-full bg-space-indigo-200"
                        aria-hidden
                      />
                      Voice
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.header>
  )
}

export default Navbar
